import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useCallback, useMemo, useState } from 'react';
import strings from '../../../common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import Section from 'components/Section/Section';
import { formatCurrency } from 'utils/validators/HelperFunctions';

type AddonCatalogItem = {
  id: number;
  name: string;
  addOnCategory?: string;
  addOnType?: string;
  price?: number;
  maxCount?: number;
  description?: string;
  bookedAddOnId?: number;
};

type BookedAddOn = {
  type: string;
  addOnId: number;
  name: string;
  quantity: number;
  price: number;
  maxCount?: number;
  id?: number;
};

type NewBookingAddonProps = {
  setBookedAddOns: (value: BookedAddOn[] | ((prev: BookedAddOn[]) => BookedAddOn[])) => void;
  addonsDropdown: AddonCatalogItem[];
  bookedAddOns: BookedAddOn[];
};

const getTypeUnitSuffix = (addOnType?: string): string => {
  if (addOnType === 'PER_QUANTITY') return strings.new_booking_addons_per_quantity;
  return strings.new_booking_addons_per_guest;
};

const getLineTotal = (item: BookedAddOn): number => {
  const price = Number(item.price) || 0;
  if (item.type === 'PER_QUANTITY') {
    return price * (Number(item.quantity) || 0);
  }
  return price;
};

const defaultQuantityForType = (addOnType?: string): number =>
  addOnType === 'PER_QUANTITY' ? 1 : 0;

export const NewBookingAddon: React.FC<NewBookingAddonProps> = ({
  setBookedAddOns,
  addonsDropdown,
  bookedAddOns,
}) => {
  const theme = useTheme();
  const [selectedSearch, setSelectedSearch] = useState('');
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseSearch, setBrowseSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [browseChecked, setBrowseChecked] = useState<Record<number, boolean>>({});

  const catalogById = useMemo(() => {
    const map = new Map<number, AddonCatalogItem>();
    (addonsDropdown ?? []).forEach(item => map.set(item.id, item));
    return map;
  }, [addonsDropdown]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    (addonsDropdown ?? []).forEach(item => {
      if (item.addOnCategory) set.add(item.addOnCategory);
    });
    return ['All', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [addonsDropdown]);

  const openBrowseDrawer = () => {
    const initial: Record<number, boolean> = {};
    (addonsDropdown ?? []).forEach(item => {
      initial[item.id] = bookedAddOns.some(b => b.addOnId === item.id);
    });
    setBrowseChecked(initial);
    setBrowseSearch('');
    setActiveCategory('All');
    setBrowseOpen(true);
  };

  const closeBrowseDrawer = () => {
    setBrowseOpen(false);
    setBrowseChecked({});
  };

  const filteredBrowseItems = useMemo(() => {
    const term = browseSearch.trim().toLowerCase();
    return (addonsDropdown ?? []).filter(item => {
      if (activeCategory !== 'All' && item.addOnCategory !== activeCategory) return false;
      if (!term) return true;
      return (
        item.name?.toLowerCase().includes(term) ||
        item.addOnCategory?.toLowerCase().includes(term) ||
        item.addOnType?.toLowerCase().includes(term)
      );
    });
  }, [addonsDropdown, browseSearch, activeCategory]);

  const browseSelectedCount = useMemo(
    () => Object.entries(browseChecked).filter(([, checked]) => checked).length,
    [browseChecked],
  );

  const filteredSelectedRows = useMemo(() => {
    const term = selectedSearch.trim().toLowerCase();
    return bookedAddOns.filter(item => {
      if (!term) return true;
      const catalog = catalogById.get(item.addOnId);
      return (
        item.name?.toLowerCase().includes(term) ||
        catalog?.addOnCategory?.toLowerCase().includes(term)
      );
    });
  }, [bookedAddOns, selectedSearch, catalogById]);

  const addOnsGrandTotal = useMemo(
    () => bookedAddOns.reduce((sum, item) => sum + getLineTotal(item), 0),
    [bookedAddOns],
  );

  const toggleBrowseItem = (id: number) => {
    setBrowseChecked(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAddSelected = () => {
    const toAdd: BookedAddOn[] = [];
    Object.entries(browseChecked).forEach(([idStr, checked]) => {
      if (!checked) return;
      const id = Number(idStr);
      if (bookedAddOns.some(b => b.addOnId === id)) return;
      const catalog = catalogById.get(id);
      if (!catalog) return;
      toAdd.push({
        type: catalog.addOnType ?? 'PER_QUANTITY',
        addOnId: catalog.id,
        maxCount: catalog.maxCount,
        name: catalog.name,
        id: catalog.bookedAddOnId,
        quantity: defaultQuantityForType(catalog.addOnType),
        price: catalog.price ?? 0,
      });
    });
    if (toAdd.length > 0) {
      setBookedAddOns(prev => [...prev, ...toAdd]);
    }
    closeBrowseDrawer();
  };

  const removeBookedAddOn = (addOnId: number) => {
    setBookedAddOns(prev => prev.filter(item => item.addOnId !== addOnId));
  };

  const updateBookedField = useCallback(
    (addOnId: number, field: 'quantity' | 'price', raw: string) => {
      if (raw !== '' && Number.isNaN(Number(raw))) return;
      const val = raw === '' ? 0 : parseInt(raw, 10);
      setBookedAddOns(prev =>
        prev.map(item => {
          if (item.addOnId !== addOnId) return item;
          if (field === 'quantity' && item.type === 'PER_QUANTITY' && val < 1 && raw !== '') {
            return { ...item, quantity: 1 };
          }
          return { ...item, [field]: val };
        }),
      );
    },
    [setBookedAddOns],
  );

  if (!addonsDropdown?.length) return null;

  const hasBookedAddOns = bookedAddOns.length > 0;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Section label={strings.menu_title_addOns} compact />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5, mt: 0.5 }}>
        {hasBookedAddOns ? (
          <TextField
            fullWidth
            size="small"
            placeholder={strings.new_booking_addons_search_selected}
            value={selectedSearch}
            onChange={e => setSelectedSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: selectedSearch ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSelectedSearch('')} aria-label="clear">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        ) : null}
        <Button
          variant="outlined"
          size="small"
          onClick={openBrowseDrawer}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap', width: hasBookedAddOns ? undefined : 'max-content' }}>
          {strings.new_booking_addons_browse}
        </Button>
      </Stack>

      {!hasBookedAddOns ? (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Box
            sx={{
              py: 2,
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}>
            <PlaylistAddOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              {strings.new_booking_addons_empty_title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.35, maxWidth: 420, mt: 0.35 }}>
              {strings.new_booking_addons_empty_description}
            </Typography>
          </Box>
        </Card>
      ) : (
        <>
          <TableContainer
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 1.5,
            }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                  <TableCell sx={{ fontWeight: 700 }}>{strings.beo_add_on_name}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{strings.addOn_column_category}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{strings.new_booking_addons_unit_price}</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 100 }}>
                    {strings.bookingDetails_text_addOn_quantity}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }} align="right">
                    {strings.new_booking_addons_total_column}
                  </TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSelectedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 3, color: 'text.secondary', textAlign: 'center' }}>
                      {strings.new_booking_addons_none_selected}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSelectedRows.map(row => {
                    const catalog = catalogById.get(row.addOnId);
                    const category = catalog?.addOnCategory ?? '—';
                    const lineTotal = getLineTotal(row);
                    return (
                      <TableRow key={row.addOnId} hover>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{category}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={row.price ?? 0}
                            onChange={e => updateBookedField(row.addOnId, 'price', e.target.value)}
                            inputProps={{ inputMode: 'numeric' }}
                            sx={{ maxWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          {row.type === 'PER_QUANTITY' ? (
                            <TextField
                              size="small"
                              value={row.quantity ?? 0}
                              onChange={e => updateBookedField(row.addOnId, 'quantity', e.target.value)}
                              inputProps={{ inputMode: 'numeric', min: 1 }}
                              sx={{ maxWidth: 88 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(lineTotal)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" padding="checkbox">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={strings.deleteText}
                            onClick={() => removeBookedAddOn(row.addOnId)}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Typography variant="body2" fontWeight={700}>
              {strings.new_booking_addons_total} {formatCurrency(addOnsGrandTotal)}
            </Typography>
          </Box>
        </>
      )}

      <DrawerMenu
        open={browseOpen}
        handleClose={closeBrowseDrawer}
        closeButton
        width={420}
        dropBack
        flexColumn
        title={strings.new_booking_addons_browse}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={strings.new_booking_addons_search_catalog}
            value={browseSearch}
            onChange={e => setBrowseSearch(e.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
            {categories.map(cat => (
              <Chip
                key={cat}
                label={cat === 'All' ? strings.new_booking_addons_all_categories : cat}
                size="small"
                clickable
                color={activeCategory === cat ? 'primary' : 'default'}
                variant={activeCategory === cat ? 'filled' : 'outlined'}
                onClick={() => setActiveCategory(cat)}
              />
            ))}
          </Stack>

          <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, mx: -0.5, px: 0.5 }}>
            {filteredBrowseItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                —
              </Typography>
            ) : (
              filteredBrowseItems.map(item => {
                const checked = !!browseChecked[item.id];
                const alreadyBooked = bookedAddOns.some(b => b.addOnId === item.id);
                return (
                  <Box
                    key={item.id}
                    onClick={() => toggleBrowseItem(item.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      py: 1.25,
                      px: 0.5,
                      cursor: 'pointer',
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                    }}>
                    <Checkbox size="small" checked={checked} sx={{ p: 0.25, mt: 0.15 }} tabIndex={-1} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} lineHeight={1.35}>
                        {item.name}
                        {alreadyBooked ? (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({strings.new_booking_addons_already_added})
                          </Typography>
                        ) : null}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.35}>
                        {item.addOnCategory ?? '—'}
                      </Typography>
                      <Typography variant="caption" color="text.primary" display="block" lineHeight={1.35} sx={{ mt: 0.25 }}>
                        {formatCurrency(item.price ?? 0)} {getTypeUnitSuffix(item.addOnType)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              pt: 1.5,
              mt: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
            }}>
            <Typography variant="body2" color="text.secondary">
              {strings.formatString(strings.new_booking_addons_selected_count, browseSelectedCount)}
            </Typography>
            <Button variant="contained" size="small" sx={{ color: '#fff' }} onClick={handleAddSelected}>
              {strings.new_booking_addons_add_selected}
            </Button>
          </Box>
        </Box>
      </DrawerMenu>
    </Box>
  );
};
